import React, { useState, useEffect } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getLoanPrograms, getWebsiteImagesPublic } from '../services/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const titleToSlug = {
  'DSCR Loans': 'dscr-loans',
  'Cash Out Refinance': 'dscr-loans',
  'Hard Money': 'hard-money',
  'Fix and Flip': 'fix-and-flip',
  'New Construction': 'new-construction',
  'Commercial or Multi-Family': 'commercial-multi-family',
  'Short Term Rental': 'short-term-rental',
};

const LoanPrograms = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
        const [programsData, imagesData] = await Promise.all([
          getLoanPrograms(),
          getWebsiteImagesPublic()
        ]);

        const programsWithImages = programsData.map((program, index) => {
          const imageData = imagesData.find(img => img.section === `loan_program_${index + 1}`);
          if (imageData) {
            const imageUrl = (imageData.imageUrl.startsWith('/api/uploads') || imageData.imageUrl.startsWith('/uploads'))
              ? `${BACKEND_URL}${imageData.imageUrl}`
              : imageData.imageUrl;
            return { ...program, image: imageUrl };
          }
          return program;
        });

        setPrograms(programsWithImages);
      } catch (err) {
        console.error('Failed to load loan programs:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPrograms();
  }, []);

  const handleGetStarted = (title) => {
    const slug = titleToSlug[title] || title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    navigate(`/loan-programs/${slug}`);
  };

  if (loading) {
    return (
      <section id="loan-programs" data-testid="loan-programs-section" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex justify-center items-center min-h-[400px]">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin" />
          </div>
        </div>
      </section>
    );
  }

  return (
    <>
      <section id="loan-programs" data-testid="loan-programs-section" className="py-24 bg-white">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <div className="mb-16 relative">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              Loan Products
            </h2>
            {/* Decorative accent dot */}
            <div className="absolute -top-2 -left-4 w-5 h-5 bg-blue-500 rounded-full opacity-60"></div>
          </div>

          {/* Products Grid - 2 columns like minolending */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {programs.map((program) => (
              <div
                key={program.id}
                data-testid={`loan-card-${program.id}`}
                className="group relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer"
                onClick={() => handleGetStarted(program.title)}
              >
                {/* Background Image */}
                <img
                  src={program.image}
                  alt={`${program.title} loans - Close Now Lending`}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                {/* Dark Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                {/* Label positioned at bottom-right */}
                <div className="absolute bottom-0 right-0 bg-white/95 backdrop-blur-sm rounded-tl-2xl p-4 md:p-6 transition-all duration-300 group-hover:bg-blue-600">
                  <div className="flex items-center gap-3">
                    <ArrowRight className="h-5 w-5 text-blue-600 group-hover:text-white transition-colors" />
                    <div>
                      <h3 className="text-sm md:text-base font-bold text-gray-900 group-hover:text-white transition-colors uppercase tracking-wide">
                        {program.title}
                      </h3>
                      <p className="text-xs text-gray-500 group-hover:text-blue-100 transition-colors">
                        Get started
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default LoanPrograms;
